import {
  adminIntakeVars,
  defaultIntakeVars,
  deleteIntakes,
  ecopetrolIntakeVars,
  kocIntakeVars,
  restoreIntakes
} from "@/models/equipment/hps_intake/predicates";
import type { Intakes } from "gql/api";
import {
  useDeleteIntakesMutation,
  useRestoreIntakesMutation,
  useUpdateIntakeByPkMutation,
  useInsertIntakeMutation,
  useHpsIntakesQuery
} from "gql/api";
import { v4 } from "uuid";
import { computed } from "vue";
import { useEquipment } from "@/composables/equipment/baseEquipment";
import type { UseEquipment } from "@/composables/equipment/equipmentTypes";
import { hpsIntakeTableColumns } from "./hpsIntakeTableColumns";
import useUserInfo from "@/composables/auth/useUserInfo";

export function useHpsIntakes(isAdmin: boolean): UseEquipment<Intakes> {
  const baseEquipment = useEquipment<Intakes>(isAdmin);
  const title = "Suction";
  const user = useUserInfo();

  const getVariables = () => {
    if (user.is_koc_customer) return kocIntakeVars;
    if (user.is_ecopetrol_customer) return ecopetrolIntakeVars;
    if (user.is_admin) return adminIntakeVars;
    return defaultIntakeVars;
  };
  const {
    data,
    executeQuery: refetch,
    stale: isStale
  } = useHpsIntakesQuery({
    variables: getVariables()
  });

  const allRows = computed(() => data.value?.intakes as Intakes[] | undefined);

  const hashOrder: (keyof Intakes)[] = [
    "manufacturer",
    "part_number",
    "intake_type",
    "configuration",
    "shaft_material",
    "shaft_limit_hp",
    "high_strength_shaft_limit_hp",
    "housing_material",
    "max_rate_bpd",
    "outer_diameter_inches"
  ];

  const { defineVisibleColumns, defineFilterData } = baseEquipment;

  const { defineUpsert, defineRestore, defineDelete } =
    baseEquipment.defineMutationHandlers(refetch, hashOrder);

  const { executeMutation: executeInsertIntake } = useInsertIntakeMutation();
  const { executeMutation: executeUpdateIntake } =
    useUpdateIntakeByPkMutation();
  const { executeMutation: executeDeleteIntake } = useDeleteIntakesMutation();
  const { executeMutation: executeRestoreIntake } = useRestoreIntakesMutation();

  const insertIntake = (intake: Omit<Intakes, "sizing_import" | "user">) =>
    executeInsertIntake({ object: intake });

  const updateIntake = (id: string, intake: Intakes) =>
    executeUpdateIntake({ id, object: intake });

  const deleteIntake = (ids: string[]) =>
    executeDeleteIntake({ where: deleteIntakes(ids) });

  const restoreIntake = (ids: string[]) =>
    executeRestoreIntake({ where: restoreIntakes(ids) });

  baseEquipment.defineEmptyRow(() => ({
    id: v4(),
    manufacturer: "",
    intake_type: "",
    part_number: "",
    outer_diameter_inches: null,
    inner_diameter_inches: null,
    neck_outer_diameter_inches: null,
    neck_inner_diameter_inches: null,
    length_inches: null,
    weight_pounds: null,
    shaft_material: "",
    shaft_limit_hp: null,
    housing_material: "",
    max_rate_bpd: null,
    high_strength_shaft_limit_hp: null,
    hp: null,
    eff1: null,
    eff2: null,
    created_at: "now()",
    updated_at: "now()",
    deleted_at: null,
    imported_sizing_id: null,
    is_hps: true
  }));

  return {
    searchPlaceholder: "Search HPS Suctions...",
    searchToolTip: `Add quotes around search terms for a more exact result. Ex: "Gas Separator"`,
    columns: defineVisibleColumns(hpsIntakeTableColumns),
    handleUpsert: defineUpsert(insertIntake, updateIntake),
    handleDelete: defineDelete(deleteIntake),
    handleRestore: defineRestore(restoreIntake),
    totalCount: computed(() => data.value?.intakes_aggregate.aggregate?.count),
    title,
    allRows,
    isAdmin,
    refetch,
    isStale,
    ...baseEquipment,
    ...defineFilterData({
      columns: hpsIntakeTableColumns,
      globalSearchFields: ["outer_diameter_inches", "intake_type"],
      allRows,
      title
    })
  };
}
